'use client';

import { Progress } from '@curology/ui-components-web/react/Progress';
import { useAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useCheckout } from '@/components/flow/steps/checkout/hooks/useCheckout';
import { CHECKOUT_MODES } from '@/components/flow/steps/checkout/types';
import { useBraintree } from '@/components/payments/hooks/useBraintree';
import { usePayPalClient } from '@/components/payments/hooks/usePayPalClient';
import { DATA_KEYS } from '@/consts/dataKeys';
import { PayPalService } from '@/services/productSelectionAndCheckoutService/payPal';

import { useCheckoutMode } from '../../../hooks/useCheckoutMode';
import { useSelectedItemIds } from '../../../hooks/useSelectedItemIds';
import { initPayPal, payPal, resetPayPalRenderState } from '../../../js/payPal';

const PayPalButton = ({
  showFlashMessageOnCheckoutSuccess,
}: {
  showFlashMessageOnCheckoutSuccess?: boolean;
}) => {
  const [showLoader, setShowLoader] = useState(true);
  const { brand, treatmentCategory } = useFlowContext();
  const checkoutMode = useCheckoutMode();
  const { patient } = useDataProvider(DATA_KEYS.productSelection);
  const { selectedItemIds } = useSelectedItemIds();
  const { onSuccess, onIneligible } = useCheckout();
  const payPalClient = usePayPalClient();
  const { clients: braintreeClients } = useBraintree();
  const hasRenderedRef = useRef(false);

  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );

  const onSubmit =
    checkoutMode === CHECKOUT_MODES.Trial
      ? PayPalService.activateExpressCheckoutTrial
      : PayPalService.activateExpressCheckoutSubscription;

  // Initial PayPal button render — only runs once when clients are ready.
  // Subsequent dependency changes are handled via refs to avoid re-rendering
  // the PayPal button, which causes "Page already has an active payment session".
  useEffect(() => {
    if (!payPalClient || !braintreeClients) {
      return;
    }

    // Only render the PayPal button once per mount cycle
    if (hasRenderedRef.current) {
      return;
    }
    hasRenderedRef.current = true;

    payPal.renderCount += 1;

    initPayPal({
      dataCollector: braintreeClients.dataCollector,
      payPalCheckout: braintreeClients.payPalCheckout,
      brand,
      flashMessages,
      patient,
      setFlashMessages,
      setShowLoader,
      showFlashMessageOnCheckoutSuccess,
      renderCount: payPal.renderCount,
      onSubmit,
      treatmentCategory,
      onSuccess,
      onIneligible,
    });
  }, [
    brand,
    flashMessages,
    patient,
    showFlashMessageOnCheckoutSuccess,
    setFlashMessages,
    onSubmit,
    onSuccess,
    onIneligible,
    treatmentCategory,
    payPalClient,
    braintreeClients,
  ]);

  // Reset rendering state on unmount so the button can re-render on re-mount
  useEffect(() => {
    return () => {
      hasRenderedRef.current = false;
      resetPayPalRenderState();
    };
  }, []);

  useEffect(() => {
    payPal.selectedItemIds = selectedItemIds;
  }, [selectedItemIds]);

  return (
    <div className="cur-text-center">
      {showLoader && (
        <Progress
          className="cur-inline-block"
          progressType="circle"
          state="highlight"
        />
      )}
      <div id="paypal-button-container" />
    </div>
  );
};

export default PayPalButton;
